import { useAuth } from "@/App";
import { useNavigate, useLocation } from "react-router-dom";
import { Users, PlusSquare, BookOpen, LogOut, Trophy, Flame, Shield, Star, Swords } from "lucide-react";

const navItems = [
  { path: "/", icon: Users, label: "I Miei Team" },
  { path: "/leagues", icon: Trophy, label: "Leghe" },
  { path: "/tournaments", icon: Swords, label: "Tornei" },
  { path: "/create-team", icon: PlusSquare, label: "Nuovo Team" },
  { path: "/star-players", icon: Star, label: "Star Player" },
  { path: "/skills", icon: BookOpen, label: "Regolamento" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#020617] pb-20 md:pb-0 md:pl-64">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex md:fixed md:left-0 md:top-0 md:bottom-0 md:w-64 md:flex-col bg-[#0a0f1a] border-r border-white/5 z-40">
        <div className="p-6 border-b border-white/5">
          <div className="flex items-center gap-3">
            <img src="/bbcoach-logo-400.png" alt="BBCoach" className="w-10 h-10 rounded-lg object-cover shadow-lg shadow-red-900/20" />
            <div>
              <h1 className="text-lg font-title tracking-wide text-white leading-none">DOPPIOTESCHIO</h1>
              <span className="text-[10px] font-bold tracking-[0.3em] text-red-500 uppercase">BBCoach 2025</span>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <button
                key={item.path}
                data-testid={`nav-${item.path.replace('/', '') || 'home'}`}
                onClick={() => navigate(item.path)}
                className={`sidebar-link w-full flex items-center gap-3 px-4 py-3 rounded-sm text-sm font-bold uppercase tracking-wider ${
                  isActive
                    ? "sidebar-link-active text-red-400"
                    : "text-slate-400 hover:text-white hover:bg-white/5"
                }`}
              >
                <item.icon className={`w-5 h-5 transition-all duration-300 ${isActive ? "drop-shadow-[0_0_6px_rgba(225,29,72,0.4)]" : ""}`} />
                {item.label}
              </button>
            );
          })}
        </nav>

        {/* Donation Banner - Desktop */}
        <div className="px-4 pb-3" data-testid="donation-banner-sidebar">
          <div className="rounded-lg bg-gradient-to-r from-amber-500/5 to-red-500/5 border border-amber-500/10 p-3 text-center">
            <p className="text-[9px] text-slate-500 uppercase tracking-wider font-bold mb-2">Supporta Doppioteschio</p>
            <div className="flex items-center justify-center gap-2">
              <a href="https://ko-fi.com/coachgabro" target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-1 bg-[#FF5E5B] text-white text-[9px] font-bold uppercase tracking-wider py-1.5 px-2.5 rounded-sm hover:brightness-110 transition-all" data-testid="kofi-link-sidebar">
                <Flame className="w-3 h-3" /> Ko-fi
              </a>
            </div>
          </div>
        </div>

        <div className="p-4 border-t border-white/5">
          {user && (
            <div className="flex items-center gap-3 mb-3 px-2">
              {user.picture ? (
                <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-xs font-bold">
                  {user.name?.[0] || "U"}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-white truncate">{user.name}</p>
                <p className="text-xs text-slate-500 truncate">{user.email}</p>
              </div>
            </div>
          )}
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-2.5 rounded-sm text-sm font-medium text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-all"
          >
            <LogOut className="w-4 h-4" />
            Esci
          </button>
          <p className="text-[9px] text-slate-600 text-center mt-3 px-2 leading-relaxed">Creata da Coach Gabro Losco 'n Fitto</p>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="md:hidden sticky top-0 z-30 bg-[#020617]/95 backdrop-blur-md border-b border-white/5 safe-area-top">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <img src="/bbcoach-logo-400.png" alt="BBCoach" className="w-8 h-8 rounded-lg object-cover" />
            <div>
              <h1 className="text-base font-title tracking-wide text-white leading-none">DOPPIOTESCHIO</h1>
              <span className="text-[8px] font-bold tracking-[0.2em] text-red-500 uppercase">BBCoach 2025</span>
            </div>
          </div>
          {user && (
            <div className="flex items-center gap-2">
              {user.picture ? (
                <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />
              ) : (
                <div className="w-7 h-7 rounded-full bg-slate-700 flex items-center justify-center text-xs font-bold">
                  {user.name?.[0]}
                </div>
              )}
              <button onClick={handleLogout} className="p-2 text-slate-400 hover:text-red-400">
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="min-h-[calc(100vh-56px)] md:min-h-screen">
        {children}
      </main>

      {/* Mobile Bottom Nav */}
      <nav className="bottom-nav md:hidden" data-testid="mobile-nav">
        <div className="flex items-center justify-around py-2 px-2">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <button
                key={item.path}
                data-testid={`mobile-nav-${item.path.replace('/', '') || 'home'}`}
                onClick={() => navigate(item.path)}
                className={`flex flex-col items-center gap-1 px-4 py-1.5 rounded-sm touch-target transition-all duration-200 ${
                  isActive ? "text-red-400 mobile-nav-active" : "text-slate-500 active:text-slate-300"
                }`}
              >
                <item.icon className={`w-5 h-5 transition-all duration-200 ${isActive ? "drop-shadow-[0_0_8px_rgba(225,29,72,0.5)]" : ""}`} />
                <span className="text-[10px] font-bold uppercase tracking-wider">{item.label}</span>
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
